/* eslint-disable react-hooks/set-state-in-effect -- step loading effects preserve mounted workflow components. */
import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { noop } from 'lodash-es';
import {
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  CONDITION_AND_MILEAGE,
  REPORT,
  LICENSE_PLATE_AND_VIN,
  PARTNER_OFFER_CONDITIONS,
  EMAIL,
  INSTANT_OFFER,
  LICENSE_PLATE_AND_VIN_SECOND_CHANCE_CREATIVE_ID,
  APPRAISER_OFFER_REPORT_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { ColorsAndOptionsStep } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/colors-and-options-step/colors-and-options-step';
import { ConditionAndMileageStep } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/condition-and-mileage-step/condition-and-mileage-step';
import { LicensePlateAndVinStepAsync } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/license-plate-and-vin-step-async';
import { OfferConditionsStepWrapper } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/offer-conditions-step/offer-conditions-step-wrapper';
import { EmailStep } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/email-step/email-step';
import { EscapeHatchAsync } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/instant-offer-step/escape-hatch/escape-hatch-async';
import { getPrequalifiedPartners } from 'site-modules/shared/utils/multi-offer/multi-offer';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  prequalificationPath as multiOfferPrequalificationPath,
  MultiOfferModel,
} from 'client/data/models/multi-offer';
import { VehicleModel } from 'client/data/models/vehicle-v2';
import { LicensePlateAndVinStepWrapper } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/license-plate-and-vin-step-wrapper';

import lazyImports from './lazy-imports';

const { InstantOfferStep } = lazyImports;

/**
 * Returns specified step.
 * @param {String} stepId
 * @param {Object} locationAndStyleProps
 * @param {Object} optionsAndColorsProps
 * @param {Object} conditionAndMileageProps
 * @param {Object} appraisalReportProps
 * @param {Object} licensePlateAndVinProps
 * @param {Object} offerConditionsProps
 * @param {Object} emailStepProps
 * @param {Object} instantOfferProps
 * @param {Object} escapeHatchProps
 * @param {Boolean} isMobile
 * @param {Boolean} isExtendedReport
 * @returns {JSX.Element}
 */
export function TradeInAppraiserStepUI({
  stepId,
  activeStepId,
  locationAndStyleProps,
  optionsAndColorsProps,
  conditionAndMileageProps,
  appraisalReportProps,
  licensePlateAndVinProps,
  offerConditionsProps,
  emailStepProps,
  instantOfferProps,
  escapeHatchProps,
  isMobile = false,
  isExtendedReport = false,
  isContrastUpdate = false,
  multiOfferPrequalification = null,
  renewalValues = null,
  ReportStep = null,
  LocationAndStyleStepComponent,
}) {
  // Determine when chunks for the lazy-loaded steps should start loading
  const shouldLoadOfferConditionsStep = activeStepId !== LOCATION_AND_STYLE; // Load after 1st step is finished
  const shouldLoadLicensePlateAndVinStep = activeStepId !== LOCATION_AND_STYLE; // Load after 1st step is finished
  const shouldLoadInstantOfferStep = ![LOCATION_AND_STYLE, COLORS_AND_OPTIONS].includes(activeStepId); // Load after the 2nd step is finished

  const [wasInstantOfferStepLoadedBefore, setWasInstantOfferStepLoadedBefore] = useState(shouldLoadInstantOfferStep);
  const [wasLicensePlateAndVinStepLoadedBefore, setWasLicensePlateAndVinStepLoadedBefore] = useState(
    shouldLoadLicensePlateAndVinStep
  );

  const instantOfferStepAppraisalReportPropsMemoized = useMemo(
    () => ({
      ...appraisalReportProps,
      onReportFormed: noop,
      stepId: INSTANT_OFFER,
      creativeId: APPRAISER_OFFER_REPORT_CREATIVE_ID,
      isReportActive: instantOfferProps.isStepActive,
    }),

    // eslint-disable-next-line react-hooks/use-memo -- props are intentionally flattened for this compatibility boundary.
    [...Object.values(appraisalReportProps), instantOfferProps.isStepActive]
  );

  // Do NOT unmount InstantOfferStep if it has ever been loaded.
  useEffect(() => {
    if (stepId === INSTANT_OFFER && shouldLoadInstantOfferStep && !wasInstantOfferStepLoadedBefore) {
      setWasInstantOfferStepLoadedBefore(true);
    }
  }, [stepId, shouldLoadInstantOfferStep, wasInstantOfferStepLoadedBefore]);

  // Do NOT unmount LicensePlateAndVinStepAsync if it has ever been mounted.
  useEffect(() => {
    if (
      stepId === LICENSE_PLATE_AND_VIN &&
      shouldLoadLicensePlateAndVinStep &&
      !wasLicensePlateAndVinStepLoadedBefore
    ) {
      setWasLicensePlateAndVinStepLoadedBefore(true);
    }
  }, [stepId, shouldLoadLicensePlateAndVinStep, wasLicensePlateAndVinStepLoadedBefore]);

  const prequalifiedPartners = getPrequalifiedPartners(multiOfferPrequalification);
  // convert to string to avoid redundant re-renders.
  const prequalifiedPartnersStr = prequalifiedPartners.join(',');

  /* READ THIS BEFORE ADDING AB TESTS ******************************************************
   *                                                                                      **
   * InstantOfferStep tends to have a lot of AB Tests going on at the same time.          **
   * To avoid a huge React tree of repeatedly nested <Experiment> and <Recipe> tags,      **
   * I have created a series of functions that call each other serially for each          **
   * test. So, if you would like to add a test, create its own `renderEval####Abtest`     **
   * function, and replace calls to `renderInstantOfferStepWithAllABTestProps()`          **
   * with calls to your new function, and call `renderInstantOfferStepWithAllABTestProps` **
   * in your new function. For clean up, do the exact opposite. This comment was added    **
   * by Allen Vartanian @allen if you have any questions about what it is trying to say.  **
   *                                                                                      **
   * ***************************************************************************************
   */

  const renderInstantOfferStepWithAllABTestProps = abTestsProps => (
    <InstantOfferStep
      {...instantOfferProps}
      isRenewalPrequalificationReady={!renewalValues || multiOfferPrequalification != null}
      isMobile={isMobile}
      appraisalReportProps={instantOfferStepAppraisalReportPropsMemoized}
      {...abTestsProps}
    />
  );

  switch (stepId) {
    case LOCATION_AND_STYLE: {
      return <LocationAndStyleStepComponent {...locationAndStyleProps} displayTipForTruckType={isExtendedReport} />;
    }
    case COLORS_AND_OPTIONS:
      return (
        <ColorsAndOptionsStep
          {...optionsAndColorsProps}
          isContrastUpdate={isContrastUpdate}
          isMobile={isMobile}
          multiOfferPrequalification={multiOfferPrequalification}
        />
      );
    case CONDITION_AND_MILEAGE:
      return <ConditionAndMileageStep {...conditionAndMileageProps} />;
    case REPORT:
      // Pass ReportStep as a prop, in order to avoid including AppraisalReportStep into the initial bundle for the extended report
      return ReportStep && <ReportStep {...appraisalReportProps} />;
    case LICENSE_PLATE_AND_VIN:
      return (
        <LicensePlateAndVinStepWrapper {...licensePlateAndVinProps}>
          {shouldLoadLicensePlateAndVinStep || wasLicensePlateAndVinStepLoadedBefore ? (
            <LicensePlateAndVinStepAsync {...licensePlateAndVinProps} isMobile={isMobile} isLicensePlateAndVinStep />
          ) : null}
        </LicensePlateAndVinStepWrapper>
      );
    case PARTNER_OFFER_CONDITIONS:
      return (
        <OfferConditionsStepWrapper
          {...offerConditionsProps}
          shouldLoadOfferConditionsStep={shouldLoadOfferConditionsStep}
        />
      );
    case EMAIL: {
      return <EmailStep {...emailStepProps} />;
    }
    case INSTANT_OFFER: {
      return shouldLoadInstantOfferStep || wasInstantOfferStepLoadedBefore ? (
        <Fragment>
          <EscapeHatchAsync
            {...escapeHatchProps}
            isMobile={isMobile}
            stepId={stepId}
            trackingCreativeId={LICENSE_PLATE_AND_VIN_SECOND_CHANCE_CREATIVE_ID}
          />
          {renderInstantOfferStepWithAllABTestProps({
            fireFbSignal: true,
            prequalifiedPartnersStr,
          })}
        </Fragment>
      ) : null;
    }
    default:
      return null;
  }
}

TradeInAppraiserStepUI.propTypes = {
  stepId: PropTypes.string.isRequired,
  activeStepId: PropTypes.string.isRequired,
  locationAndStyleProps: PropTypes.shape({}).isRequired,
  optionsAndColorsProps: PropTypes.shape({}).isRequired,
  conditionAndMileageProps: PropTypes.shape({}).isRequired,
  appraisalReportProps: PropTypes.shape({}).isRequired,
  licensePlateAndVinProps: PropTypes.shape({}).isRequired,
  offerConditionsProps: PropTypes.shape({}).isRequired,
  emailStepProps: PropTypes.shape({}).isRequired,
  instantOfferProps: PropTypes.shape({
    isStepActive: PropTypes.bool,
  }).isRequired,
  escapeHatchProps: PropTypes.shape({}).isRequired,
  LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  isMobile: PropTypes.bool,
  isExtendedReport: PropTypes.bool,
  isContrastUpdate: PropTypes.bool,
  vehicle: PropTypes.shape({}),
  multiOfferPrequalification: PropTypes.shape({}),
  renewalValues: PropTypes.shape({ zipCode: PropTypes.string }),
  ReportStep: PropTypes.node,
};

export const mapStateToProps = state => ({
  stateCode: state?.visitor?.location?.stateCode,
  dma: state?.visitor?.location?.dma,
  visitorZipCode: state?.visitor?.location?.zipCode,
});

export const stateToPropsConfig = {
  multiOfferPrequalification: bindToPath(
    ({ vehicle, stateCode, dma, isExtendedReport, activeStepId, mileage, renewalValues, visitorZipCode }) => {
      if (renewalValues && visitorZipCode !== renewalValues.zipCode) return null;
      const isStep3Finished = ![LOCATION_AND_STYLE, COLORS_AND_OPTIONS, CONDITION_AND_MILEAGE].includes(activeStepId);
      if (isExtendedReport && isStep3Finished) {
        const year = vehicle?.style?.year;
        const makeSlug = vehicle?.style?.makeSlug;
        if (stateCode && dma && year && mileage) {
          return multiOfferPrequalificationPath.build({ stateCode, dma, year, mileage, makeSlug });
        }
      }
      return null;
    },
    MultiOfferModel
  ),
  // IMPORTANT: this API call loads vehicle photo that is used in AppraisalReportStep (see EMO-1927 bug).
  vehiclePhoto: bindToPath(({ vehicle, activeStepId }) => {
    const isLocationStepActive = activeStepId === LOCATION_AND_STYLE;
    if (!isLocationStepActive) {
      const styleId = vehicle?.style?.id;
      return styleId ? `styles.${styleId}.photo` : '';
    }
    return null;
  }, VehicleModel),
};

export const TradeInAppraiserStep = connect(mapStateToProps)(
  connectToModel(TradeInAppraiserStepUI, stateToPropsConfig)
);
